import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const POPUP_EVENTS = [
  {
    id: 'xi',
    image: '/images/tryouts/popup_tryout_xi.png',
    url: 'https://www.ticketmaster.cl/event/xi-tryout-nacional-btalentstar-cancha-el-mirador-puerto-varas',
    altKey: 'tryouts.popupAltXi' as const,
  },
  {
    id: 'xii',
    image: '/images/tryouts/popup_tryout_xii.png',
    url: 'https://www.ticketmaster.cl/event/xii-tryout-nacional-btalentstar-santa-esther-sport',
    altKey: 'tryouts.popupAltXii' as const,
  },
];

const SESSION_KEY = 'tryout_xi_xii_popup_seen_v1';
const DELAY_MS = 800;

export function TryoutPopup() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const timer = setTimeout(() => setIsOpen(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setIsOpen(false);
  };

  const handleImageClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={t('tryouts.popupHint')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto overscroll-contain px-4 pb-6 pt-16 sm:items-center sm:p-6"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/75 backdrop-blur-sm"
            onClick={handleClose}
          />

          <motion.button
            type="button"
            onClick={handleClose}
            autoFocus
            whileHover={{ scale: 1.08, rotate: 90 }}
            whileTap={{ scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            className="fixed right-3 top-[max(0.75rem,env(safe-area-inset-top))] z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-brand-darkNavy shadow-[0_4px_20px_rgba(0,0,0,0.45)] transition-colors hover:bg-brand-gold hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold sm:right-5 sm:top-5"
            aria-label={t('tryouts.popupClose')}
          >
            <X size={22} strokeWidth={2.5} />
          </motion.button>

          <motion.div
            initial={{ scale: 0.82, opacity: 0, y: 32 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.88, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28, delay: 0.05 }}
            className="relative z-10 my-auto w-full max-w-xs sm:max-w-3xl"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              {POPUP_EVENTS.map((event, index) => (
                <motion.div
                  key={event.id}
                  onClick={() => handleImageClick(event.url)}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.08 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="cursor-pointer overflow-hidden rounded-2xl shadow-[0_8px_48px_rgba(0,0,0,0.6)] ring-2 ring-brand-gold/30 transition-all hover:ring-brand-gold/70"
                >
                  <img
                    src={event.image}
                    alt={t(event.altKey)}
                    className="mx-auto block h-auto max-h-[38svh] w-full object-contain sm:max-h-[70vh]"
                    draggable={false}
                  />
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="mt-3 text-center text-xs tracking-wide text-white/70"
            >
              {t('tryouts.popupHint')}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
